import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import Loading from 'src/pages/Status/Loading';
import NotFound from 'src/pages/Status/NotFound';
import Forbidden from 'src/pages/Status/Forbidden';
import { isForbiddenError } from 'src/utils/queryClient';
import {
  AffiliationCountryList,
  fetchAffiliationCountryList,
  fetchSubmissionContext
} from '../api/queries';
import { useParams } from 'react-router-dom';
import {
  ISubmissionContext,
  ITransitionGraph
} from '@publishing-engine/domain/models';

type ISubmissionContextDataType = ISubmissionContext & {
  workflow: { transitionGraph: ITransitionGraph };
  articleTypes: {
    articleTypeCategoryId: string;
    articleWorkflowId: string;
    name: string;
    description: string;
    formSchemas: {
      authorSubmission: string;
      editorRevisionRequest: string;
      authorRevision: string;
      reviewerFirstAssessment: string;
      reviewerRevisionAssessment: string;
    };
  }[];
};
interface ISubmissionContextData {
  submissionContext: ISubmissionContextDataType | null | undefined;
  countriesData: AffiliationCountryList[] | null | undefined;
}

export const SubmissionDataContext = createContext<
  ISubmissionContextData | undefined
>(undefined);

export const useSubmissionContextData = (): ISubmissionContextData => {
  const context = useContext(SubmissionDataContext);
  if (!context) {
    throw new Error(
      'useSubmissionContextData must be used within a SubmissionContexDataProvider'
    );
  }
  return context;
};

const AuthorSubmissionFormDataProvider = ({
  children
}: {
  children: JSX.Element;
}) => {
  const { ctxId = '' } = useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['submission-context', ctxId],
    queryFn: () => (ctxId ? fetchSubmissionContext(ctxId) : null)
  });

  const { data: countriesData } = useQuery({
    queryKey: ['affiliation-country-list'],
    queryFn: () => fetchAffiliationCountryList()
  });

  // Handle all states before rendering children
  if (isLoading) return <Loading />;
  if (isError) return isForbiddenError(error) ? <Forbidden /> : <NotFound />;

  return (
    <SubmissionDataContext.Provider
      value={{
        submissionContext: data,
        countriesData: countriesData?.items
      }}
    >
      {children}
    </SubmissionDataContext.Provider>
  );
};

export default AuthorSubmissionFormDataProvider;
